import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Box,
  Grid,
  TextField,
  CircularProgress,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Sparkles,
  Edit3,
  Trash2,
  Plus,
  Search,
  X,
  Server,
  Package,
  ChevronsUpDown,
  CheckCircle2,
  RotateCcw,
  ArrowUpDown,
  CircleDot,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal,
  Check,
  Globe,
  ChevronsRight,
  Ban,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getProcessorCatalogPlans, getServiceProviders } from '../../../api/services';

export default function MultiProcessorRoutingCard({
  useV2Engine,
  onChangeUseV2Engine,
  processorRoutes,
  onChangeRoutes,
  selectedService = 'Data',
  providers = [],
  onSyncPlan,
  onPrimaryPlanChange,
  onPrimaryNetworkChange,
  fallbackPlanId = '',
  fallbackNetworkId = '',
  fallbackBuyingAmount = '',
  title = '4. Multi-Processor Routing (V2 Engine)',
  subtitle = 'Configure automated priority failover routes across connected vendors.',
}) {
  const navigate = useNavigate();

  // Provider Catalog State
  const [catalogPlans, setCatalogPlans] = useState({});
  const [loadingCatalog, setLoadingCatalog] = useState({});

  const catalogPlansRef = useRef({});
  catalogPlansRef.current = catalogPlans;

  // Internal provider management (ensures providers are always loaded)
  const [internalProviders, setInternalProviders] = useState(providers || []);

  useEffect(() => {
    if (providers && providers.length > 0) {
      setInternalProviders(providers);
    } else {
      getServiceProviders()
        .then((res) => {
          if (res.ok && res.data?.data) {
            setInternalProviders(res.data.data);
          }
        })
        .catch(() => {});
    }
  }, [providers]);

  // Tenant-configured service providers ONLY (no un-setup system templates)
  const configuredProviders = useMemo(() => {
    const list = [];
    const seen = new Set();

    (internalProviders || []).forEach((p) => {
      const s = (p.slug || '').toLowerCase().trim();
      if (s && !seen.has(s)) {
        seen.add(s);
        list.push({
          id: p.id,
          slug: s,
          name: p.name || s.toUpperCase(),
          driverType: (p.driverType || 'custom').toLowerCase(),
          balance: Number(p.balance || 0),
          lowBalanceThreshold: Number(p.lowBalanceThreshold || 5000),
          isActive: p.isActive !== false,
          isHealthy: p.isHealthy !== false,
          totalSpent: Number(p.totalSpent || 0),
        });
      }
    });

    return list;
  }, [internalProviders]);

  // Provider Selection Modal State
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [targetRouteIndex, setTargetRouteIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [balanceFilter, setBalanceFilter] = useState('all'); // 'all' | 'funded' | 'low'

  // Vendor Product Selection Modal State
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [targetPlanRouteIndex, setTargetPlanRouteIndex] = useState(null);
  const [planSearchQuery, setPlanSearchQuery] = useState('');
  const [networkFilter, setNetworkFilter] = useState('all'); // 'all' | 'MTN' | 'AIRTEL' | etc.
  const [planTypeFilter, setPlanTypeFilter] = useState('all'); // 'all' | specific dataType
  const [planSortBy, setPlanSortBy] = useState('default'); // 'default' | 'price_asc' | 'price_desc' | 'name_asc'
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState(null);
  const [priorityMenuRouteIdx, setPriorityMenuRouteIdx] = useState(null);

  // Counts for status filters
  const statusCounts = useMemo(() => {
    let active = 0;
    let inactive = 0;
    configuredProviders.forEach((p) => {
      if (p.isActive) {
        active += 1;
      } else {
        inactive += 1;
      }
    });
    return { all: configuredProviders.length, active, inactive };
  }, [configuredProviders]);

  // Filtered providers list in modal
  const filteredProviders = useMemo(
    () =>
      configuredProviders.filter((p) => {
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          const matchesName = p.name.toLowerCase().includes(q);
          const matchesSlug = p.slug.toLowerCase().includes(q);
          if (!matchesName && !matchesSlug) {
            return false;
          }
        }

        if (statusFilter === 'active' && !p.isActive) return false;
        if (statusFilter === 'inactive' && p.isActive) return false;

        if (balanceFilter === 'funded' && p.balance <= 0) return false;
        if (balanceFilter === 'low' && p.balance >= p.lowBalanceThreshold) return false;

        return true;
      }),
    [configuredProviders, searchQuery, statusFilter, balanceFilter]
  );

  const isFiltered = Boolean(
    searchQuery.trim() || statusFilter !== 'all' || balanceFilter !== 'all'
  );

  const handleResetFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setBalanceFilter('all');
  };

  const handleOpenProviderModal = (index) => {
    setTargetRouteIndex(index);
    setSearchQuery('');
    setProviderModalOpen(true);
  };

  // Category mapping for ProcessorPlan query
  const mappedCategory = useMemo(() => {
    if (selectedService === 'ResultCheck') return 'result_check';
    return (selectedService || 'data').toLowerCase();
  }, [selectedService]);

  // Lazy load plans for a processor slug
  const loadCatalogPlansForSlug = useCallback(
    async (slug, categoryOverride, forceRefresh = false) => {
      const cleanSlug = (slug || '').toLowerCase().trim();
      if (!cleanSlug) return;
      const cat = categoryOverride || mappedCategory;
      const cacheKey = `${cleanSlug}_${cat}`;
      const collapsedSlug = cleanSlug.replace(/[^a-z0-9]/g, '');
      const collapsedKey = `${collapsedSlug}_${cat}`;

      if (!forceRefresh) {
        if (catalogPlansRef.current[cacheKey]?.length > 0) return;
        if (collapsedSlug && catalogPlansRef.current[collapsedKey]?.length > 0) {
          setCatalogPlans((prev) => {
            const updated = { ...prev, [cacheKey]: prev[collapsedKey] };
            catalogPlansRef.current = updated;
            return updated;
          });
          return;
        }
      }

      setLoadingCatalog((prev) => ({ ...prev, [cleanSlug]: true }));
      try {
        const res = await getProcessorCatalogPlans(cleanSlug, {
          category: cat,
          limit: 500,
        });
        const responseData = res.data?.data;
        let plans = [];
        if (responseData && Array.isArray(responseData.plans)) {
          const { plans: pList } = responseData;
          plans = pList;
        } else if (Array.isArray(responseData)) {
          plans = responseData;
        }

        // Fallback: If 0 plans and slug has hyphens/underscores, query with collapsed alphanumeric slug
        if ((!plans || plans.length === 0) && collapsedSlug && collapsedSlug !== cleanSlug) {
          try {
            const fbRes = await getProcessorCatalogPlans(collapsedSlug, {
              category: cat,
              limit: 500,
            });
            const fbData = fbRes.data?.data;
            if (fbData && Array.isArray(fbData.plans) && fbData.plans.length > 0) {
              const { plans: fbList } = fbData;
              plans = fbList;
            } else if (Array.isArray(fbData) && fbData.length > 0) {
              plans = fbData;
            }
          } catch (fbErr) {
            // ignore fallback error
          }
        }

        setCatalogPlans((prev) => {
          const updated = {
            ...prev,
            [cacheKey]: plans,
            ...(collapsedSlug ? { [collapsedKey]: plans } : {}),
          };
          catalogPlansRef.current = updated;
          return updated;
        });
      } catch (err) {
        let fallbackPlans = [];
        if (collapsedSlug && collapsedSlug !== cleanSlug) {
          try {
            const fbRes = await getProcessorCatalogPlans(collapsedSlug, {
              category: cat,
              limit: 500,
            });
            const fbData = fbRes.data?.data;
            if (fbData && Array.isArray(fbData.plans)) {
              const { plans: fbList } = fbData;
              fallbackPlans = fbList;
            } else if (Array.isArray(fbData)) {
              fallbackPlans = fbData;
            }
          } catch (e2) {
            // ignore fallback error
          }
        }

        setCatalogPlans((prev) => {
          const updated = {
            ...prev,
            [cacheKey]: fallbackPlans,
            ...(collapsedSlug ? { [collapsedKey]: fallbackPlans } : {}),
          };
          catalogPlansRef.current = updated;
          return updated;
        });
      } finally {
        setLoadingCatalog((prev) => ({ ...prev, [cleanSlug]: false }));
      }
    },
    [mappedCategory]
  );

  // Preload plans for any routes configured
  useEffect(() => {
    processorRoutes.forEach((route) => {
      if (route.processorSlug) {
        loadCatalogPlansForSlug(route.processorSlug, mappedCategory);
      }
    });
  }, [processorRoutes, mappedCategory, loadCatalogPlansForSlug]);

  // Handle provider selection from modal
  const handleSelectProvider = (provider) => {
    if (targetRouteIndex === null) return;
    const { slug, name } = provider;

    const updated = processorRoutes.map((r, rIdx) => {
      if (rIdx !== targetRouteIndex) return r;
      return {
        ...r,
        processorSlug: slug,
        planId: '',
        costPrice: undefined,
        catalogPlan: null,
      };
    });

    onChangeRoutes(updated);

    if (targetRouteIndex === 0 && onPrimaryPlanChange) {
      onPrimaryPlanChange('');
    }

    loadCatalogPlansForSlug(slug, mappedCategory, true);
    setProviderModalOpen(false);
    toast.info(`Selected provider ${name} (@${slug})`);
  };

  // Active route & catalog data for the Plan Selection Modal
  const activePlanRoute =
    targetPlanRouteIndex !== null ? processorRoutes[targetPlanRouteIndex] : null;
  const activePlanRouteSlug = (activePlanRoute?.processorSlug || '').toLowerCase();
  const activePlanRoutePlans = useMemo(
    () => {
      const clean = activePlanRouteSlug.trim();
      const collapsed = clean.replace(/[^a-z0-9]/g, '');
      return (
        catalogPlans[`${clean}_${mappedCategory}`] ||
        (collapsed ? catalogPlans[`${collapsed}_${mappedCategory}`] : null) ||
        []
      );
    },
    [catalogPlans, activePlanRouteSlug, mappedCategory]
  );
  const isLoadingActivePlanCatalog = Boolean(loadingCatalog[activePlanRouteSlug]);

  // Available telecom networks in this vendor's catalog (e.g. MTN, Airtel, Glo, 9mobile)
  const availablePlanNetworks = useMemo(() => {
    const knownNetworks = ['MTN', 'AIRTEL', 'GLO', '9MOBILE'];
    const detected = new Set();
    activePlanRoutePlans.forEach((plan) => {
      const text = `${plan.dataType || ''} ${plan.planName || ''}`.toUpperCase();
      knownNetworks.forEach((net) => {
        if (text.includes(net)) {
          detected.add(net);
        }
      });
    });
    // Return only if at least 2 distinct networks are found in this catalog
    if (detected.size >= 2) {
      return Array.from(detected);
    }
    return [];
  }, [activePlanRoutePlans]);

  // Counts of plans per network
  const networkCounts = useMemo(() => {
    const counts = { all: activePlanRoutePlans.length };
    availablePlanNetworks.forEach((net) => {
      counts[net] = 0;
    });
    activePlanRoutePlans.forEach((plan) => {
      const text = `${plan.dataType || ''} ${plan.planName || ''}`.toUpperCase();
      availablePlanNetworks.forEach((net) => {
        if (text.includes(net)) {
          counts[net] += 1;
        }
      });
    });
    return counts;
  }, [activePlanRoutePlans, availablePlanNetworks]);

  // Total plans matching current network filter
  const networkFilteredPlansCount = useMemo(() => {
    if (networkFilter === 'all') return activePlanRoutePlans.length;
    return activePlanRoutePlans.filter((p) => {
      const text = `${p.dataType || ''} ${p.planName || ''}`.toUpperCase();
      return text.includes(networkFilter.toUpperCase());
    }).length;
  }, [activePlanRoutePlans, networkFilter]);

  // Distinct category/dataTypes options (and their counts) under current network filter
  const planDataTypeOptions = useMemo(() => {
    const map = new Map();
    activePlanRoutePlans.forEach((p) => {
      if (!p.dataType || typeof p.dataType !== 'string') return;
      const dType = p.dataType.trim();
      if (!dType) return;

      if (networkFilter !== 'all') {
        const text = `${p.dataType || ''} ${p.planName || ''}`.toUpperCase();
        if (!text.includes(networkFilter.toUpperCase())) {
          return;
        }
      }
      map.set(dType, (map.get(dType) || 0) + 1);
    });

    return Array.from(map.entries()).map(([name, count]) => ({
      name,
      count,
    }));
  }, [activePlanRoutePlans, networkFilter]);

  // Filtered & sorted products list in modal
  const filteredPlanList = useMemo(() => {
    let list = activePlanRoutePlans.filter((plan) => {
      if (planSearchQuery.trim()) {
        const q = planSearchQuery.toLowerCase().trim();
        const matchesName = (plan.planName || '').toLowerCase().includes(q);
        const matchesId = String(plan.planId || '').toLowerCase().includes(q);
        const matchesType = (plan.dataType || '').toLowerCase().includes(q);
        const matchesValidity = (plan.validity || '').toLowerCase().includes(q);
        if (!matchesName && !matchesId && !matchesType && !matchesValidity) {
          return false;
        }
      }

      if (networkFilter !== 'all') {
        const text = `${plan.dataType || ''} ${plan.planName || ''}`.toUpperCase();
        if (!text.includes(networkFilter.toUpperCase())) {
          return false;
        }
      }

      if (planTypeFilter !== 'all') {
        const pType = (plan.dataType || '').toLowerCase();
        if (pType !== planTypeFilter.toLowerCase()) {
          return false;
        }
      }

      return true;
    });

    if (planSortBy === 'price_asc') {
      list = [...list].sort((a, b) => Number(a.costPrice || 0) - Number(b.costPrice || 0));
    } else if (planSortBy === 'price_desc') {
      list = [...list].sort((a, b) => Number(b.costPrice || 0) - Number(a.costPrice || 0));
    } else if (planSortBy === 'name_asc') {
      list = [...list].sort((a, b) => (a.planName || '').localeCompare(b.planName || ''));
    }

    return list;
  }, [activePlanRoutePlans, planSearchQuery, networkFilter, planTypeFilter, planSortBy]);

  const isPlanFiltered = Boolean(
    planSearchQuery.trim() ||
      networkFilter !== 'all' ||
      planTypeFilter !== 'all' ||
      planSortBy !== 'default'
  );

  const handleResetPlanFilters = () => {
    setPlanSearchQuery('');
    setNetworkFilter('all');
    setPlanTypeFilter('all');
    setPlanSortBy('default');
  };

  const handleOpenPlanModal = (index) => {
    const route = processorRoutes[index];
    if (!route?.processorSlug) {
      toast.warning('Please select a service provider first.');
      return;
    }
    setTargetPlanRouteIndex(index);
    setPlanSearchQuery('');
    setNetworkFilter('all');
    setPlanTypeFilter('all');
    setPlanSortBy('default');
    setCategoryMenuAnchor(null);
    setPlanModalOpen(true);
    loadCatalogPlansForSlug(route.processorSlug, mappedCategory, true);
  };

  const handleSelectPlan = (plan) => {
    if (targetPlanRouteIndex === null) return;
    const routeIdx = targetPlanRouteIndex;

    let detectedNetId;
    if (plan.networkId !== undefined && plan.networkId !== null) {
      detectedNetId = String(plan.networkId);
    } else if (plan.network_id !== undefined && plan.network_id !== null) {
      detectedNetId = String(plan.network_id);
    } else if (plan.raw?.networkId !== undefined) {
      detectedNetId = String(plan.raw.networkId);
    } else if (plan.raw?.network !== undefined) {
      detectedNetId = String(plan.raw.network);
    }

    const updated = processorRoutes.map((r, rIdx) => {
      if (rIdx !== routeIdx) return r;
      return {
        ...r,
        planId: plan.planId,
        networkId: detectedNetId !== undefined ? detectedNetId : r.networkId,
        costPrice: plan.costPrice,
        catalogPlan: plan,
        isCustomId: false,
      };
    });

    onChangeRoutes(updated);

    if (routeIdx === 0) {
      if (onPrimaryPlanChange) {
        onPrimaryPlanChange(String(plan.planId));
      }
      if (detectedNetId !== undefined && onPrimaryNetworkChange) {
        onPrimaryNetworkChange(detectedNetId);
      }
      if (onSyncPlan) {
        onSyncPlan({ plan, routeIndex: routeIdx, isManualSync: false });
      }
    }

    setPlanModalOpen(false);
    toast.success(`Selected ${plan.planName} (ID: ${plan.planId})`);
  };

  // Add Route to V2 Router
  const handleAddRoute = () => {
    const usedSlugs = new Set(
      processorRoutes.map((r) => (r.processorSlug || '').toLowerCase().trim())
    );
    const unusedProvider = configuredProviders.find((p) => !usedSlugs.has(p.slug));
    const defaultProvider = unusedProvider?.slug || configuredProviders[0]?.slug || 'kybdata';

    const nextPriority = processorRoutes.length + 1;
    const newRoute = {
      priority: nextPriority,
      processorSlug: defaultProvider,
      planId: fallbackPlanId || '',
      networkId: fallbackNetworkId ? String(fallbackNetworkId) : undefined,
      costPrice: undefined,
      retryOnFailure: true,
      maxRetries: 0,
      onFailureAction: 'next_processor',
      enabled: true,
      isCustomId: false,
    };
    const updated = [...processorRoutes, newRoute];
    onChangeRoutes(updated);
    loadCatalogPlansForSlug(defaultProvider, mappedCategory);
  };

  // Remove Route from V2 Router
  const handleRemoveRoute = (index) => {
    const filtered = processorRoutes.filter((_, idx) => idx !== index);
    const updated = filtered.map((r, idx) => ({ ...r, priority: idx + 1 }));
    onChangeRoutes(updated);
  };

  // Route field change
  const handleRouteFieldChange = (index, field, value) => {
    const updated = processorRoutes.map((r, idx) => {
      if (idx !== index) return r;
      const modified = { ...r, [field]: value };
      if (field === 'onFailureAction') {
        modified.retryOnFailure = value !== 'abort';
      }
      return modified;
    });
    onChangeRoutes(updated);
    if (index === 0 && field === 'planId' && onPrimaryPlanChange) {
      onPrimaryPlanChange(value);
    }
  };

  // Move Route to change priority
  const handleMoveRoute = (fromIdx, toIdx) => {
    if (
      fromIdx === null ||
      toIdx === null ||
      toIdx < 0 ||
      toIdx >= processorRoutes.length ||
      fromIdx === toIdx
    ) {
      return;
    }
    const list = [...processorRoutes];
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    const updated = list.map((r, idx) => ({ ...r, priority: idx + 1 }));
    onChangeRoutes(updated);
    if ((fromIdx === 0 || toIdx === 0) && onPrimaryPlanChange) {
      onPrimaryPlanChange(String(updated[0]?.planId || ''));
    }
    const vendorName = moved.processorSlug ? moved.processorSlug.toUpperCase() : 'Route';
    toast.info(`${vendorName} moved to Priority ${toIdx + 1}`);
  };

  const getRouteExecutionLabel = (idx, isLegacy) => {
    if (isLegacy) return 'Single Execution';
    if (idx === 0) return 'Primary Route';
    return `Failover #${idx}`;
  };

  const getModalTargetRouteTitle = () => {
    if (targetRouteIndex === null) return 'Configure route vendor';
    if (targetRouteIndex === 0) return 'Primary Execution Route (Priority 1)';
    return `Failover Route #${targetRouteIndex} (Priority ${targetRouteIndex + 1})`;
  };

  const getModalPlanTargetRouteTitle = () => {
    if (targetPlanRouteIndex === null) return 'Choose product from vendor catalog';
    const vendorName = activePlanRouteSlug ? activePlanRouteSlug.toUpperCase() : 'Vendor';
    if (targetPlanRouteIndex === 0) {
      return `Select ${vendorName} product for Primary Execution (Priority 1)`;
    }
    return `Select ${vendorName} product for Failover Route #${targetPlanRouteIndex} (Priority ${targetPlanRouteIndex + 1})`;
  };

  const getUsedInOtherRouteLabel = (slug) => {
    if (targetRouteIndex === null) return null;
    const cleanSlug = (slug || '').toLowerCase();
    const otherIdx = processorRoutes.findIndex(
      (r, rIdx) => rIdx !== targetRouteIndex && (r.processorSlug || '').toLowerCase() === cleanSlug
    );
    if (otherIdx === -1) return null;
    if (otherIdx === 0) return 'In Primary Route';
    return `In Priority ${otherIdx + 1}`;
  };

  const renderPlanCatalogContent = () => {
    if (isLoadingActivePlanCatalog) {
      return (
        <Box sx={{ py: 9, textAlign: 'center' }}>
          <CircularProgress size={32} sx={{ mb: 2, color: '#0071E3' }} />
          <Typography sx={{ color: '#86868B', fontSize: '0.875rem', fontWeight: 500 }}>
            Fetching live products catalog from {activePlanRouteSlug.toUpperCase()}...
          </Typography>
        </Box>
      );
    }

    if (filteredPlanList.length > 0) {
      return (
        <Grid container spacing={1.5}>
          {filteredPlanList.map((plan) => {
            const isSelected = selectedPlanIdInTargetRoute === String(plan.planId);

            return (
              <Grid item xs={12} sm={6} key={plan._id || plan.planId}>
                <Box
                  onClick={() => handleSelectPlan(plan)}
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    border: '1.5px solid',
                    borderColor: isSelected ? '#0071E3' : 'rgba(0, 0, 0, 0.08)',
                    bgcolor: isSelected ? 'rgba(0, 113, 227, 0.03)' : '#FFFFFF',
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                    boxShadow: isSelected
                      ? '0 4px 16px rgba(0, 113, 227, 0.12)'
                      : '0 1px 3px rgba(0, 0, 0, 0.02)',
                    '&:hover': {
                      borderColor: '#0071E3',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.07)',
                    },
                  }}
                >
                  {/* Top: Product Name, Code, Badges */}
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.25}>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '11px',
                          bgcolor: isSelected ? 'rgba(0, 113, 227, 0.12)' : 'rgba(0, 113, 227, 0.07)',
                          color: '#0071E3',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Package size={19} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: '#1D1D1F',
                            lineHeight: 1.25,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {plan.planName}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center" mt={0.5}>
                          <Box
                            sx={{
                              px: 0.75,
                              py: 0.15,
                              borderRadius: '6px',
                              bgcolor: 'rgba(118, 118, 128, 0.1)',
                              color: '#48484A',
                              fontSize: '0.675rem',
                              fontWeight: 700,
                            }}
                          >
                            ID: {plan.planId}
                          </Box>
                          {plan.dataType && (
                            <Box
                              sx={{
                                px: 0.75,
                                py: 0.15,
                                borderRadius: '6px',
                                bgcolor: 'rgba(255, 149, 0, 0.1)',
                                color: '#FF9500',
                                fontSize: '0.675rem',
                                fontWeight: 700,
                              }}
                            >
                              {plan.dataType}
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    </Stack>

                    {isSelected && (
                      <Box sx={{ color: '#0071E3', display: 'flex', alignItems: 'center' }}>
                        <CheckCircle2 size={20} />
                      </Box>
                    )}
                  </Stack>

                  {/* Price & Validity Well */}
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: '12px',
                      bgcolor: 'rgba(118, 118, 128, 0.05)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 1.25,
                    }}
                  >
                    <div>
                      <Typography sx={{ fontSize: '0.68rem', color: '#86868B', fontWeight: 600 }}>
                        Vendor Buying Price
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '0.975rem',
                          color: '#34C759',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        ₦{Number(plan.costPrice || 0).toLocaleString()}
                      </Typography>
                    </div>
                    {plan.validity && (
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: '8px',
                          bgcolor: '#FFFFFF',
                          border: '1px solid rgba(0, 0, 0, 0.06)',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          color: '#636366',
                        }}
                      >
                        {plan.validity}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      );
    }

    return (
      <Box
        sx={{
          py: 7,
          textAlign: 'center',
          bgcolor: 'rgba(118, 118, 128, 0.05)',
          borderRadius: '20px',
        }}
      >
        <Package size={38} color="#86868B" style={{ marginBottom: 12 }} />
        <Typography sx={{ fontWeight: 650, color: '#1D1D1F', mb: 0.5, fontSize: '0.95rem' }}>
          No Products Found
        </Typography>
        <Typography sx={{ fontSize: '0.825rem', color: '#86868B', mb: 2.5 }}>
          {activePlanRoutePlans.length === 0
            ? `No ${selectedService} products synced for ${activePlanRouteSlug.toUpperCase()}. You can type the code manually.`
            : 'No products match your current search or plan type filter.'}
        </Typography>
        {isPlanFiltered ? (
          <Button
            variant="outlined"
            size="small"
            onClick={handleResetPlanFilters}
            startIcon={<RotateCcw size={14} />}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'rgba(0, 0, 0, 0.15)',
              color: '#1D1D1F',
            }}
          >
            Clear Search & Filters
          </Button>
        ) : (
          <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
            <Button
              variant="contained"
              size="small"
              startIcon={<RotateCcw size={14} />}
              onClick={() => {
                if (activePlanRouteSlug) {
                  loadCatalogPlansForSlug(activePlanRouteSlug, mappedCategory, true);
                }
              }}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 650,
                bgcolor: '#0071E3',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#005BB5' },
              }}
            >
              Reload Catalog
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit3 size={14} />}
              onClick={() => {
                if (targetPlanRouteIndex !== null) {
                  handleRouteFieldChange(targetPlanRouteIndex, 'isCustomId', true);
                }
                setPlanModalOpen(false);
              }}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'rgba(0, 0, 0, 0.15)',
                color: '#1D1D1F',
              }}
            >
              Enter Code Manually
            </Button>
          </Stack>
        )}
      </Box>
    );
  };

  const renderRouteCard = (route, idx, isLegacy = false) => {
    const routeSlug = (route.processorSlug || '').toLowerCase().trim();
    const collapsedSlug = routeSlug.replace(/[^a-z0-9]/g, '');
    const currentPlans =
      catalogPlans[`${routeSlug}_${mappedCategory}`] ||
      (collapsedSlug ? catalogPlans[`${collapsedSlug}_${mappedCategory}`] : null) ||
      [];
    const isLoadingPlans = Boolean(loadingCatalog[routeSlug]);
    const isCustom = Boolean(route.isCustomId) || (currentPlans.length === 0 && !isLoadingPlans && Boolean(route.planId));

    const selectedPlanObj =
      currentPlans.find((cp) => String(cp.planId) === String(route.planId)) || null;

    const executionLabel = getRouteExecutionLabel(idx, isLegacy);
    const matchedProvider = configuredProviders.find((p) => p.slug === routeSlug);
    const isLowBalance = matchedProvider ? matchedProvider.balance < matchedProvider.lowBalanceThreshold : false;

    let selectedPlanName = '';
    if (selectedPlanObj) {
      selectedPlanName = selectedPlanObj.planName;
    } else if (route.planId) {
      selectedPlanName = `Product Code #${route.planId}`;
    }

    let displayedCost = 0;
    if (route.costPrice !== undefined && route.costPrice !== null && route.costPrice !== '') {
      displayedCost = Number(route.costPrice);
    } else if (selectedPlanObj && selectedPlanObj.costPrice !== undefined) {
      displayedCost = Number(selectedPlanObj.costPrice);
    }

    let formattedBalance = '—';
    if (matchedProvider) {
      formattedBalance = `₦${matchedProvider.balance.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    let providerDisplayTitle = 'Vendor';
    if (matchedProvider) {
      providerDisplayTitle = matchedProvider.name;
    } else if (routeSlug) {
      providerDisplayTitle = routeSlug.toUpperCase();
    }

    let syncStatusText = `No catalog cached for ${providerDisplayTitle}`;
    if (currentPlans.length > 0) {
      syncStatusText = `${currentPlans.length} products synced from ${providerDisplayTitle}`;
    }

    const isRouteEnabled = route.enabled !== false;
    const maxRetries = Number(route.maxRetries ?? 0);
    const onFailureAction =
      route.onFailureAction ||
      (route.retryOnFailure === false ? 'abort' : 'next_processor');

    let priorityBadgeBg = 'rgba(118, 118, 128, 0.12)';
    let priorityBadgeColor = '#8E8E93';
    if (isRouteEnabled) {
      priorityBadgeBg = idx === 0 ? 'rgba(0, 113, 227, 0.08)' : 'rgba(118, 118, 128, 0.08)';
      priorityBadgeColor = idx === 0 ? '#0071E3' : '#48484A';
    }

    return (
      <Box
        key={idx}
        sx={{
          p: { xs: 2, sm: 2.75 },
          borderRadius: '20px',
          bgcolor: isRouteEnabled ? '#FFFFFF' : '#FAFAFC',
          opacity: isRouteEnabled ? 1 : 0.78,
          border: isRouteEnabled ? '1px solid rgba(0, 0, 0, 0.08)' : '1.5px dashed rgba(118, 118, 128, 0.35)',
          boxShadow: isRouteEnabled ? '0 2px 10px rgba(0, 0, 0, 0.02)' : 'none',
          transition: 'all 0.2s ease',
        }}
      >
        {/* Route Card Top Header: Priority Badge & Actions */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
            {!isLegacy && processorRoutes.length > 1 ? (
              <Button
                size="small"
                onClick={(e) => {
                  setPriorityMenuAnchor(e.currentTarget);
                  setPriorityMenuRouteIdx(idx);
                }}
                endIcon={<ChevronDown size={13} style={{ marginLeft: -3 }} />}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.25,
                  py: 0.35,
                  borderRadius: '8px',
                  bgcolor: priorityBadgeBg,
                  color: priorityBadgeColor,
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  textTransform: 'none',
                  border: '1px solid',
                  borderColor: isRouteEnabled ? 'rgba(0, 113, 227, 0.2)' : 'rgba(118, 118, 128, 0.2)',
                  minWidth: 0,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isRouteEnabled ? 'rgba(0, 113, 227, 0.15)' : 'rgba(118, 118, 128, 0.18)',
                    transform: 'translateY(-0.5px)',
                  },
                }}
              >
                Priority {route.priority}
              </Button>
            ) : (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.25,
                  py: 0.45,
                  borderRadius: '8px',
                  bgcolor: priorityBadgeBg,
                  color: priorityBadgeColor,
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                }}
              >
                {isLegacy ? 'Single Execution' : `Priority ${route.priority}`}
              </Box>
            )}

            {!isLegacy && processorRoutes.length > 1 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(118, 118, 128, 0.08)',
                  borderRadius: '8px',
                  p: '2px',
                  gap: '1px',
                }}
              >
                <IconButton
                  size="small"
                  title="Move Up (Higher Priority)"
                  disabled={idx === 0}
                  onClick={() => handleMoveRoute(idx, idx - 1)}
                  sx={{
                    width: 24,
                    height: 24,
                    p: 0,
                    color: '#1D1D1F',
                    borderRadius: '6px',
                    transition: 'all 0.15s ease',
                    '&:hover': { bgcolor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' },
                    '&.Mui-disabled': { color: '#C7C7CC' },
                  }}
                >
                  <ChevronUp size={14} />
                </IconButton>
                <IconButton
                  size="small"
                  title="Move Down (Lower Priority)"
                  disabled={idx === processorRoutes.length - 1}
                  onClick={() => handleMoveRoute(idx, idx + 1)}
                  sx={{
                    width: 24,
                    height: 24,
                    p: 0,
                    color: '#1D1D1F',
                    borderRadius: '6px',
                    transition: 'all 0.15s ease',
                    '&:hover': { bgcolor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' },
                    '&.Mui-disabled': { color: '#C7C7CC' },
                  }}
                >
                  <ChevronDown size={14} />
                </IconButton>
              </Box>
            )}

            {!isRouteEnabled && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 0.9,
                  py: 0.2,
                  borderRadius: '6px',
                  bgcolor: 'rgba(255, 59, 48, 0.1)',
                  color: '#FF3B30',
                  fontSize: '0.68rem',
                  fontWeight: 750,
                  letterSpacing: '0.04em',
                }}
              >
                DISABLED
              </Box>
            )}

            <Typography
              sx={{
                fontSize: '0.78rem',
                color: '#86868B',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <span>•</span>
              <span>{executionLabel}</span>
              {idx === 0 && !isLegacy && (
                <span style={{ fontSize: '0.72rem', color: isRouteEnabled ? '#34C759' : '#8E8E93', fontWeight: 600 }}>
                  (Initial Attempt)
                </span>
              )}
              {idx > 0 && (
                <span style={{ fontSize: '0.72rem', color: '#86868B' }}>
                  (Triggers on Provider Failover)
                </span>
              )}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.25}>
            {!isLegacy && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.35,
                  borderRadius: '9px',
                  bgcolor: isRouteEnabled ? 'rgba(52, 199, 89, 0.08)' : 'rgba(118, 118, 128, 0.08)',
                  border: '1px solid',
                  borderColor: isRouteEnabled ? 'rgba(52, 199, 89, 0.22)' : 'rgba(118, 118, 128, 0.18)',
                  transition: 'all 0.15s ease',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: isRouteEnabled ? '#2D8A4E' : '#636366',
                    userSelect: 'none',
                  }}
                >
                  {isRouteEnabled ? 'Enabled' : 'Disabled'}
                </Typography>
                <Switch
                  size="small"
                  checked={isRouteEnabled}
                  onChange={(e) => handleRouteFieldChange(idx, 'enabled', e.target.checked)}
                  sx={{
                    width: 32,
                    height: 18,
                    p: 0,
                    '& .MuiSwitch-switchBase': {
                      p: '2px',
                      '&.Mui-checked': {
                        transform: 'translateX(14px)',
                        color: '#FFFFFF',
                        '& + .MuiSwitch-track': {
                          bgcolor: '#34C759',
                          opacity: 1,
                        },
                      },
                    },
                    '& .MuiSwitch-thumb': {
                      width: 14,
                      height: 14,
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 9,
                      bgcolor: 'rgba(118, 118, 128, 0.3)',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            )}

            {!isLegacy && (
              <Button
                size="small"
                startIcon={<Trash2 size={13} />}
                onClick={() => handleRemoveRoute(idx)}
                disabled={processorRoutes.length <= 1}
                sx={{
                  color: '#FF3B30',
                  bgcolor: 'rgba(255, 59, 48, 0.06)',
                  borderRadius: '8px',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  py: 0.4,
                  px: 1.25,
                  minWidth: 0,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 59, 48, 0.14)',
                  },
                  '&.Mui-disabled': {
                    opacity: 0.25,
                    bgcolor: 'transparent',
                    color: '#8E8E93',
                  },
                }}
              >
                Remove
              </Button>
            )}
          </Stack>
        </Stack>

        {!isRouteEnabled && (
          <Box
            sx={{
              mb: 2,
              px: 1.5,
              py: 0.85,
              borderRadius: '11px',
              bgcolor: 'rgba(255, 149, 0, 0.06)',
              border: '1px solid rgba(255, 149, 0, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AlertCircle size={15} color="#FF9500" style={{ flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#D97706', fontWeight: 600 }}>
              This route is currently disabled and will be bypassed during checkout execution. Turn the switch back on to restore routing through this provider.
            </Typography>
          </Box>
        )}

        {/* Two Balanced Columns: Service Provider (50%) & Vendor Product (50%) */}
        <Grid container spacing={2}>
          {/* Tile 1: Service Provider Tile */}
          <Grid item xs={12} md={6}>
            <Box
              onClick={() => handleOpenProviderModal(idx)}
              sx={{
                p: 2,
                borderRadius: '16px',
                border: '1px solid',
                borderColor: matchedProvider ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 113, 227, 0.35)',
                bgcolor: '#FFFFFF',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  borderColor: '#0071E3',
                  boxShadow: '0 6px 20px rgba(0, 113, 227, 0.10)',
                  transform: 'translateY(-1.5px)',
                  '& .apple-change-badge': {
                    bgcolor: 'rgba(0, 113, 227, 0.14)',
                    color: '#0071E3',
                  },
                },
                '&:active': {
                  transform: 'scale(0.994)',
                },
              }}
            >
              {/* Tile Header */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: matchedProvider?.isActive ? '#34C759' : '#8E8E93',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: '#86868B',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    Service Provider
                  </Typography>
                </Stack>

                <Box
                  className="apple-change-badge"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.3,
                    borderRadius: '6px',
                    bgcolor: 'rgba(0, 113, 227, 0.08)',
                    color: '#0071E3',
                    fontSize: '0.7rem',
                    fontWeight: 650,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span>{matchedProvider ? 'Change' : 'Select'}</span>
                  <ChevronsUpDown size={12} />
                </Box>
              </Stack>

              {/* Tile Body: Avatar & Name */}
              {matchedProvider ? (
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, my: 0.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      background: 'linear-gradient(180deg, #2C2C2E 0%, #1C1C1E 100%)',
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      flexShrink: 0,
                      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 2px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {matchedProvider.name.charAt(0).toUpperCase()}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        color: '#1D1D1F',
                        lineHeight: 1.25,
                        letterSpacing: '-0.01em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {matchedProvider.name}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.75} mt={0.35}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#86868B', fontWeight: 500 }}>
                        @{matchedProvider.slug}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 0.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      bgcolor: 'rgba(0, 113, 227, 0.08)',
                      color: '#0071E3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Server size={22} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 650, color: '#1D1D1F', lineHeight: 1.25 }}>
                      Choose Service Provider
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#86868B', mt: 0.25 }}>
                      Select from configured tenant vendors
                    </Typography>
                  </Box>
                </Stack>
              )}

              {/* Bottom Inset Metric Well: Provider Balance */}
              <Box
                sx={{
                  mt: 2,
                  px: 1.5,
                  py: 1,
                  borderRadius: '11px',
                  bgcolor: isLowBalance ? 'rgba(255, 149, 0, 0.05)' : 'rgba(52, 199, 89, 0.05)',
                  border: isLowBalance ? '1px solid rgba(255, 149, 0, 0.18)' : '1px solid rgba(52, 199, 89, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography sx={{ fontSize: '0.72rem', color: isLowBalance ? '#FF9500' : '#2D8A4E', fontWeight: 600 }}>
                  {isLowBalance ? 'Low Wallet Balance' : 'Wallet Balance'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    color: isLowBalance ? '#FF9500' : '#34C759',
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {formattedBalance}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Tile 2: Vendor Product Tile */}
          <Grid item xs={12} md={6}>
            {!isCustom ? (
              <Box
                onClick={() => handleOpenPlanModal(idx)}
                sx={{
                  p: 2,
                  borderRadius: '16px',
                  border: '1px solid',
                  borderColor: route.planId ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 113, 227, 0.35)',
                  bgcolor: '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  '&:hover': {
                    borderColor: '#0071E3',
                    boxShadow: '0 6px 20px rgba(0, 113, 227, 0.10)',
                    transform: 'translateY(-1.5px)',
                    '& .apple-change-badge': {
                      bgcolor: 'rgba(0, 113, 227, 0.14)',
                      color: '#0071E3',
                    },
                  },
                  '&:active': {
                    transform: 'scale(0.994)',
                  },
                }}
              >
                {/* Tile Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: currentPlans.length > 0 ? '#34C759' : '#8E8E93',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: '#86868B',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      Vendor Product
                    </Typography>
                  </Stack>

                  <Box
                    className="apple-change-badge"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.3,
                      borderRadius: '6px',
                      bgcolor: 'rgba(0, 113, 227, 0.08)',
                      color: '#0071E3',
                      fontSize: '0.7rem',
                      fontWeight: 650,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span>{route.planId ? 'Change' : 'Browse Catalog'}</span>
                    <ChevronsUpDown size={12} />
                  </Box>
                </Stack>

                {/* Tile Body: Product Icon & Info */}
                {route.planId ? (
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, my: 0.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        background: 'linear-gradient(180deg, #0A84FF 0%, #0071E3 100%)',
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 2px 6px rgba(0, 113, 227, 0.25)',
                      }}
                    >
                      <Package size={22} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          color: '#1D1D1F',
                          lineHeight: 1.25,
                          letterSpacing: '-0.01em',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {selectedPlanName}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.65} mt={0.35} flexWrap="wrap">
                        <Box
                          sx={{
                            px: 0.75,
                            py: 0.15,
                            borderRadius: '5px',
                            bgcolor: 'rgba(118, 118, 128, 0.08)',
                            color: '#48484A',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                          }}
                        >
                          #{route.planId}
                        </Box>
                        {selectedPlanObj?.dataType && (
                          <Box
                            sx={{
                              px: 0.75,
                              py: 0.15,
                              borderRadius: '5px',
                              bgcolor: 'rgba(255, 149, 0, 0.1)',
                              color: '#FF9500',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                            }}
                          >
                            {selectedPlanObj.dataType}
                          </Box>
                        )}
                        {(route.networkId || selectedPlanObj?.networkId) && (
                          <Box
                            sx={{
                              px: 0.75,
                              py: 0.15,
                              borderRadius: '5px',
                              bgcolor: 'rgba(0, 113, 227, 0.08)',
                              color: '#0071E3',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              letterSpacing: '0.02em',
                            }}
                          >
                            Net ID: {route.networkId || selectedPlanObj?.networkId}
                          </Box>
                        )}
                        {selectedPlanObj?.validity && (
                          <Typography sx={{ fontSize: '0.75rem', color: '#86868B', fontWeight: 500 }}>
                            • {selectedPlanObj.validity}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 0.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        bgcolor: 'rgba(0, 113, 227, 0.08)',
                        color: '#0071E3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Package size={22} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 650, color: '#1D1D1F', lineHeight: 1.25 }}>
                        Choose Vendor Product
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#86868B', mt: 0.25 }}>
                        {isLoadingPlans ? 'Loading vendor catalog...' : `${currentPlans.length} products available in catalog`}
                      </Typography>
                    </Box>
                  </Stack>
                )}

                {/* Bottom Inset Metric Well: Vendor Buying Cost */}
                <Box
                  sx={{
                    mt: 2,
                    px: 1.5,
                    py: 1,
                    borderRadius: '11px',
                    bgcolor: 'rgba(52, 199, 89, 0.05)',
                    border: '1px solid rgba(52, 199, 89, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography sx={{ fontSize: '0.72rem', color: '#2D8A4E', fontWeight: 600 }}>
                    Vendor Buying Cost
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.88rem',
                      fontWeight: 700,
                      color: '#34C759',
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    ₦{displayedCost.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            ) : (
              /* Custom Plan ID Manual Code Mode */
              <Box
                sx={{
                  p: 2,
                  borderRadius: '16px',
                  border: '1px solid rgba(0, 113, 227, 0.25)',
                  bgcolor: '#FFFFFF',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#0071E3' }} />
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: '#86868B',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      Manual Vendor Product Code
                    </Typography>
                  </Stack>
                  {currentPlans.length > 0 && (
                    <Button
                      size="small"
                      startIcon={<Package size={11} />}
                      onClick={() => handleRouteFieldChange(idx, 'isCustomId', false)}
                      sx={{
                        py: 0.25,
                        px: 1,
                        borderRadius: '6px',
                        bgcolor: 'rgba(0, 113, 227, 0.08)',
                        color: '#0071E3',
                        fontSize: '0.7rem',
                        fontWeight: 650,
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'rgba(0, 113, 227, 0.15)' },
                      }}
                    >
                      Catalog Browser
                    </Button>
                  )}
                </Stack>

                <Grid container spacing={1.25} sx={{ my: 'auto' }}>
                  <Grid item xs={12} sm={4.5}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Plan ID / Code"
                      placeholder="e.g. 1, DATA1000"
                      value={route.planId || ''}
                      onChange={(e) => handleRouteFieldChange(idx, 'planId', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '11px',
                          bgcolor: '#FFFFFF',
                          fontSize: '0.82rem',
                          '&.Mui-focused fieldset': { borderColor: '#0071E3' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3.5}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Network ID"
                      placeholder={fallbackNetworkId ? `Def (${fallbackNetworkId})` : 'e.g. 1'}
                      value={route.networkId !== undefined && route.networkId !== null ? route.networkId : ''}
                      onChange={(e) => handleRouteFieldChange(idx, 'networkId', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '11px',
                          bgcolor: '#FFFFFF',
                          fontSize: '0.82rem',
                          '&.Mui-focused fieldset': { borderColor: '#0071E3' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Cost Price (₦)"
                      placeholder="0.00"
                      value={route.costPrice !== undefined ? route.costPrice : ''}
                      onChange={(e) => handleRouteFieldChange(idx, 'costPrice', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '11px',
                          bgcolor: '#FFFFFF',
                          fontSize: '0.82rem',
                          '&.Mui-focused fieldset': { borderColor: '#0071E3' },
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 1.5,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '9px',
                    bgcolor: 'rgba(118, 118, 128, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography sx={{ fontSize: '0.7rem', color: '#86868B', fontWeight: 500 }}>
                    Direct code execution (no catalog mapping required)
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Failover & Retry Policy Well */}
        {!isLegacy && (
          <Box
            sx={{
              mt: 2,
              p: 1.75,
              borderRadius: '16px',
              bgcolor: 'rgba(118, 118, 128, 0.035)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Setting 1: Retry Attempts on Failure */}
              <Grid item xs={12} md={6}>
                <Stack spacing={0.75}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <RotateCcw size={13} color="#0071E3" />
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: '#1D1D1F',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Retry Attempts on Failure
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      bgcolor: 'rgba(118, 118, 128, 0.09)',
                      borderRadius: '10px',
                      p: '3px',
                      display: 'flex',
                      gap: '2px',
                    }}
                  >
                    {[
                      { value: 0, label: '0 (No retry)' },
                      { value: 1, label: '1 retry' },
                      { value: 2, label: '2 retries' },
                      { value: 3, label: '3 retries' },
                    ].map((item) => {
                      const isSelected = maxRetries === item.value;
                      return (
                        <Button
                          key={item.value}
                          size="small"
                          onClick={() => handleRouteFieldChange(idx, 'maxRetries', item.value)}
                          sx={{
                            flex: 1,
                            py: 0.4,
                            px: 0.5,
                            borderRadius: '7px',
                            fontSize: '0.72rem',
                            fontWeight: isSelected ? 700 : 500,
                            color: isSelected ? '#1D1D1F' : '#636366',
                            bgcolor: isSelected ? '#FFFFFF' : 'transparent',
                            boxShadow: isSelected ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              bgcolor: isSelected ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
                            },
                          }}
                        >
                          {item.label}
                        </Button>
                      );
                    })}
                  </Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#86868B', lineHeight: 1.25 }}>
                    {maxRetries === 0
                      ? 'Tries once only. On vendor error, immediately proceeds to the failure action.'
                      : `Will retry up to ${maxRetries} additional time${maxRetries > 1 ? 's' : ''} on connection or provider failure.`}
                  </Typography>
                </Stack>
              </Grid>

              {/* Setting 2: Action After Retries Exhausted */}
              <Grid item xs={12} md={6}>
                <Stack spacing={0.75}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <SlidersHorizontal size={13} color="#0071E3" />
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: '#1D1D1F',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Action After Retries Exhausted
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      bgcolor: 'rgba(118, 118, 128, 0.09)',
                      borderRadius: '10px',
                      p: '3px',
                      display: 'flex',
                      gap: '2px',
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => handleRouteFieldChange(idx, 'onFailureAction', 'next_processor')}
                      sx={{
                        flex: 1,
                        py: 0.4,
                        px: 1,
                        borderRadius: '7px',
                        fontSize: '0.72rem',
                        fontWeight: onFailureAction === 'next_processor' ? 700 : 500,
                        color: onFailureAction === 'next_processor' ? '#0071E3' : '#636366',
                        bgcolor: onFailureAction === 'next_processor' ? '#FFFFFF' : 'transparent',
                        boxShadow: onFailureAction === 'next_processor' ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.6,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: onFailureAction === 'next_processor' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
                        },
                      }}
                    >
                      <ChevronsRight size={13} />
                      <span>Move to Next Route</span>
                    </Button>

                    <Button
                      size="small"
                      onClick={() => handleRouteFieldChange(idx, 'onFailureAction', 'abort')}
                      sx={{
                        flex: 1,
                        py: 0.4,
                        px: 1,
                        borderRadius: '7px',
                        fontSize: '0.72rem',
                        fontWeight: onFailureAction === 'abort' ? 700 : 500,
                        color: onFailureAction === 'abort' ? '#FF3B30' : '#636366',
                        bgcolor: onFailureAction === 'abort' ? '#FFFFFF' : 'transparent',
                        boxShadow: onFailureAction === 'abort' ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.6,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: onFailureAction === 'abort' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
                        },
                      }}
                    >
                      <Ban size={13} />
                      <span>Stop & Fail Transaction</span>
                    </Button>
                  </Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#86868B', lineHeight: 1.25 }}>
                    {onFailureAction === 'next_processor'
                      ? 'Fails over to the next priority vendor route in the list if attempts fail.'
                      : 'Transaction fails immediately when this vendor fails. Does not attempt next route.'}
                  </Typography>
                </Stack>
              </Grid>

              {/* Setting 3: Custom Vendor Network ID Override */}
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5, borderColor: 'rgba(0, 0, 0, 0.05)' }} />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Globe size={13} color="#0071E3" />
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          color: '#1D1D1F',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        Vendor Network ID (Payload Override)
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.68rem', color: '#86868B', mt: 0.25 }}>
                      The network code sent in payload to this vendor (e.g. 1 for MTN, 2 for Airtel). Leave blank to use catalog or service default.
                    </Typography>
                  </Box>

                  <TextField
                    size="small"
                    placeholder={fallbackNetworkId ? `Default (${fallbackNetworkId})` : 'e.g. 1'}
                    value={route.networkId !== undefined && route.networkId !== null ? route.networkId : ''}
                    onChange={(e) => handleRouteFieldChange(idx, 'networkId', e.target.value)}
                    sx={{
                      width: { xs: '100%', sm: 170 },
                      flexShrink: 0,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '9px',
                        bgcolor: '#FFFFFF',
                        fontSize: '0.8rem',
                        height: 34,
                      },
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Sub-Actions Toolbar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
          sx={{
            mt: 2,
            pt: 1.5,
            borderTop: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Left: Sync Status */}
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: currentPlans.length > 0 ? '#34C759' : '#8E8E93',
              }}
            />
            <Typography sx={{ fontSize: '0.75rem', color: '#86868B', fontWeight: 500 }}>
              {syncStatusText}
            </Typography>
          </Stack>

          {/* Right: Quick Action Capsules */}
          <Stack direction="row" spacing={1} alignItems="center">
            {selectedPlanObj && onSyncPlan && (
              <Button
                size="small"
                startIcon={<Sparkles size={12} />}
                onClick={() => {
                  onSyncPlan({ plan: selectedPlanObj, routeIndex: idx, isManualSync: true });
                  toast.success(`Synced name & cost from ${selectedPlanObj.planName}!`);
                }}
                sx={{
                  py: 0.45,
                  px: 1.5,
                  borderRadius: '12px',
                  bgcolor: 'rgba(0, 113, 227, 0.08)',
                  fontSize: '0.75rem',
                  color: '#0071E3',
                  fontWeight: 650,
                  textTransform: 'none',
                  boxShadow: 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'rgba(0, 113, 227, 0.16)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Sync to Form
              </Button>
            )}

            {!isCustom ? (
              <Button
                size="small"
                startIcon={<Edit3 size={12} />}
                onClick={() => handleRouteFieldChange(idx, 'isCustomId', true)}
                sx={{
                  py: 0.45,
                  px: 1.5,
                  borderRadius: '12px',
                  bgcolor: 'rgba(118, 118, 128, 0.08)',
                  fontSize: '0.75rem',
                  color: '#48484A',
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'rgba(118, 118, 128, 0.16)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Manual Code
              </Button>
            ) : (
              <Button
                size="small"
                startIcon={<Package size={12} />}
                onClick={() => handleRouteFieldChange(idx, 'isCustomId', false)}
                sx={{
                  py: 0.45,
                  px: 1.5,
                  borderRadius: '12px',
                  bgcolor: 'rgba(0, 113, 227, 0.08)',
                  fontSize: '0.75rem',
                  color: '#0071E3',
                  fontWeight: 650,
                  textTransform: 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'rgba(0, 113, 227, 0.16)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Browse Catalog
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    );
  };

  const defaultProviderSlug = configuredProviders[0]?.slug || 'kybdata';
  const legacyRoute = processorRoutes[0] || {
    priority: 1,
    processorSlug: defaultProviderSlug,
    planId: fallbackPlanId || '',
    networkId: fallbackNetworkId ? String(fallbackNetworkId) : undefined,
    costPrice: fallbackBuyingAmount ? Number(fallbackBuyingAmount) : undefined,
    isCustomId: false,
  };

  let selectedSlugInTargetRoute = '';
  if (targetRouteIndex !== null && processorRoutes[targetRouteIndex]?.processorSlug) {
    selectedSlugInTargetRoute = processorRoutes[targetRouteIndex].processorSlug.toLowerCase();
  }

  let selectedPlanIdInTargetRoute = '';
  if (targetPlanRouteIndex !== null && processorRoutes[targetPlanRouteIndex]?.planId) {
    selectedPlanIdInTargetRoute = String(processorRoutes[targetPlanRouteIndex].planId);
  }

  return (
    <Card
      sx={{
        p: 3.5,
        borderRadius: '24px',
        bgcolor: '#FFFFFF',
        border: '1px solid rgba(0, 0, 0, 0.07)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
        mb: 3.5,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <div>
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#86868B',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ color: '#636366', fontSize: '0.875rem', mt: 0.5 }}>
            {subtitle}
          </Typography>
        </div>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(useV2Engine)}
              onChange={(e) => onChangeUseV2Engine(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography sx={{ fontWeight: 650, fontSize: '0.875rem', color: '#1D1D1F' }}>
              {useV2Engine ? 'V2 Router Active' : 'Legacy Single Mode'}
            </Typography>
          }
        />
      </Stack>

      <Divider sx={{ my: 2.5, borderColor: 'rgba(0, 0, 0, 0.06)' }} />

      {useV2Engine ? (
        <Stack spacing={2}>
          {processorRoutes.length > 0 && processorRoutes.every((r) => r.enabled === false) && (
            <Alert
              severity="warning"
              icon={<AlertCircle size={18} />}
              sx={{
                borderRadius: '16px',
                fontSize: '0.825rem',
                bgcolor: 'rgba(255, 149, 0, 0.08)',
                color: '#D97706',
                border: '1px solid rgba(255, 149, 0, 0.25)',
              }}
            >
              <strong>All processor routes are currently disabled.</strong> Transactions for this service will fail until at least one route is re-enabled.
            </Alert>
          )}
          {processorRoutes.map((route, idx) => renderRouteCard(route, idx, false))}

          <Button
            variant="outlined"
            startIcon={<Plus size={16} />}
            onClick={handleAddRoute}
            sx={{
              borderRadius: '14px',
              textTransform: 'none',
              fontWeight: 650,
              color: '#0071E3',
              borderColor: 'rgba(0, 113, 227, 0.3)',
              bgcolor: 'rgba(0, 113, 227, 0.04)',
              alignSelf: 'flex-start',
              px: 2,
              py: 0.75,
              '&:hover': {
                bgcolor: 'rgba(0, 113, 227, 0.08)',
                borderColor: '#0071E3',
              },
            }}
          >
            Add Failover Route
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2}>
          {renderRouteCard(legacyRoute, 0, true)}
          <Alert severity="info" sx={{ borderRadius: '16px', fontSize: '0.85rem', bgcolor: 'rgba(0, 113, 227, 0.06)', color: '#0071E3' }}>
            In Legacy Single Mode, orders execute directly through this vendor. Switch on{' '}
            <strong>V2 Router Active</strong> above to configure automatic multi-vendor failovers.
          </Alert>
        </Stack>
      )}

      {/* Service Provider Selection Modal - Apple Sheet Modal */}
      <Dialog
        open={providerModalOpen}
        onClose={() => setProviderModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            boxShadow: '0 30px 80px -20px rgba(0, 0, 0, 0.25)',
            maxHeight: '90vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 3, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '12px',
                  bgcolor: 'rgba(0, 113, 227, 0.08)',
                  color: '#0071E3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Server size={22} />
              </Box>
              <div>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#1D1D1F', letterSpacing: '-0.02em' }}>
                  Choose Service Provider
                </Typography>
                <Typography variant="body2" sx={{ color: '#86868B', fontSize: '0.825rem' }}>
                  {getModalTargetRouteTitle()}
                </Typography>
              </div>
            </Stack>

            <IconButton
              size="small"
              onClick={() => setProviderModalOpen(false)}
              sx={{
                width: 30,
                height: 30,
                bgcolor: 'rgba(118, 118, 128, 0.12)',
                color: '#1D1D1F',
                '&:hover': { bgcolor: 'rgba(118, 118, 128, 0.2)' },
              }}
            >
              <X size={16} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Stack spacing={2.5}>
            {configuredProviders.length > 0 && (
              <>
                {/* Apple-style Search Bar */}
                <TextField
              fullWidth
              size="small"
              placeholder="Search by provider name or slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={17} color="#86868B" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <X size={15} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: 'rgba(118, 118, 128, 0.08)',
                  '& fieldset': { borderColor: 'transparent' },
                  '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.1)' },
                  '&.Mui-focused': {
                    bgcolor: '#FFFFFF',
                    '& fieldset': { borderColor: '#0071E3', borderWidth: '1.5px' },
                    boxShadow: '0 0 0 3px rgba(0, 113, 227, 0.15)',
                  },
                },
              }}
            />

            {/* Apple Segmented Filter Toolbar */}
            <Stack spacing={1.5}>
              <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
                {/* Status Segmented Control */}
                <Box
                  sx={{
                    bgcolor: 'rgba(118, 118, 128, 0.1)',
                    borderRadius: '10px',
                    p: '3px',
                    display: 'inline-flex',
                    gap: '2px',
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => setStatusFilter('all')}
                    sx={{
                      px: 1.5,
                      py: 0.35,
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: statusFilter === 'all' ? 700 : 500,
                      color: statusFilter === 'all' ? '#1D1D1F' : '#636366',
                      bgcolor: statusFilter === 'all' ? '#FFFFFF' : 'transparent',
                      boxShadow: statusFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      textTransform: 'none',
                    }}
                  >
                    All ({statusCounts.all})
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setStatusFilter('active')}
                    sx={{
                      px: 1.5,
                      py: 0.35,
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: statusFilter === 'active' ? 700 : 500,
                      color: statusFilter === 'active' ? '#1D1D1F' : '#636366',
                      bgcolor: statusFilter === 'active' ? '#FFFFFF' : 'transparent',
                      boxShadow: statusFilter === 'active' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      textTransform: 'none',
                    }}
                  >
                    Active Only ({statusCounts.active})
                  </Button>
                  {statusCounts.inactive > 0 && (
                    <Button
                      size="small"
                      onClick={() => setStatusFilter('inactive')}
                      sx={{
                        px: 1.5,
                        py: 0.35,
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: statusFilter === 'inactive' ? 700 : 500,
                        color: statusFilter === 'inactive' ? '#1D1D1F' : '#636366',
                        bgcolor: statusFilter === 'inactive' ? '#FFFFFF' : 'transparent',
                        boxShadow: statusFilter === 'inactive' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        textTransform: 'none',
                      }}
                    >
                      Inactive ({statusCounts.inactive})
                    </Button>
                  )}
                </Box>

                {/* Balance Segmented Control */}
                <Box
                  sx={{
                    bgcolor: 'rgba(118, 118, 128, 0.1)',
                    borderRadius: '10px',
                    p: '3px',
                    display: 'inline-flex',
                    gap: '2px',
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => setBalanceFilter('all')}
                    sx={{
                      px: 1.5,
                      py: 0.35,
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: balanceFilter === 'all' ? 700 : 500,
                      color: balanceFilter === 'all' ? '#1D1D1F' : '#636366',
                      bgcolor: balanceFilter === 'all' ? '#FFFFFF' : 'transparent',
                      boxShadow: balanceFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      textTransform: 'none',
                    }}
                  >
                    All Balances
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setBalanceFilter('funded')}
                    sx={{
                      px: 1.5,
                      py: 0.35,
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: balanceFilter === 'funded' ? 700 : 500,
                      color: balanceFilter === 'funded' ? '#1D1D1F' : '#636366',
                      bgcolor: balanceFilter === 'funded' ? '#FFFFFF' : 'transparent',
                      boxShadow: balanceFilter === 'funded' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      textTransform: 'none',
                    }}
                  >
                    Funded (&gt; ₦0)
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setBalanceFilter('low')}
                    sx={{
                      px: 1.5,
                      py: 0.35,
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: balanceFilter === 'low' ? 700 : 500,
                      color: balanceFilter === 'low' ? '#1D1D1F' : '#636366',
                      bgcolor: balanceFilter === 'low' ? '#FFFFFF' : 'transparent',
                      boxShadow: balanceFilter === 'low' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      textTransform: 'none',
                    }}
                  >
                    Low Balance
                  </Button>
                </Box>
              </Stack>
            </Stack>

                {/* Results Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography sx={{ fontSize: '0.8rem', color: '#86868B', fontWeight: 500 }}>
                    Showing <strong>{filteredProviders.length}</strong> of {configuredProviders.length} configured service providers
                  </Typography>
                  {isFiltered && (
                    <Button
                      size="small"
                      startIcon={<RotateCcw size={12} />}
                      onClick={handleResetFilters}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', p: 0, color: '#0071E3', fontWeight: 600 }}
                    >
                      Reset Filters
                    </Button>
                  )}
                </Stack>
              </>
            )}

            {/* Provider Grid */}
            {filteredProviders.length > 0 ? (
              <Grid container spacing={1.5}>
                {filteredProviders.map((provider) => {
                  const isSelected = selectedSlugInTargetRoute === provider.slug;
                  const isLow = provider.balance < provider.lowBalanceThreshold;
                  const usedInOtherRoute = getUsedInOtherRouteLabel(provider.slug);

                  return (
                    <Grid item xs={12} sm={6} key={provider.slug}>
                      <Box
                        onClick={() => handleSelectProvider(provider)}
                        sx={{
                          p: 2,
                          borderRadius: '16px',
                          border: '1.5px solid',
                          borderColor: isSelected ? '#0071E3' : 'rgba(0, 0, 0, 0.08)',
                          bgcolor: isSelected ? 'rgba(0, 113, 227, 0.03)' : '#FFFFFF',
                          cursor: 'pointer',
                          transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                          boxShadow: isSelected
                            ? '0 4px 16px rgba(0, 113, 227, 0.12)'
                            : '0 1px 3px rgba(0, 0, 0, 0.02)',
                          '&:hover': {
                            borderColor: '#0071E3',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.07)',
                          },
                        }}
                      >
                        {/* Top: Avatar, Name, Slug, Status */}
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.25}>
                          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '11px',
                                bgcolor: '#1D1D1F',
                                color: '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                flexShrink: 0,
                              }}
                            >
                              {provider.name.charAt(0).toUpperCase()}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.925rem', color: '#1D1D1F', lineHeight: 1.2 }}>
                                {provider.name}
                              </Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: '#86868B' }}>
                                @{provider.slug}
                              </Typography>
                            </Box>
                          </Stack>

                          {isSelected && (
                            <Box sx={{ color: '#0071E3', display: 'flex', alignItems: 'center' }}>
                              <CheckCircle2 size={18} />
                            </Box>
                          )}
                        </Stack>

                        {/* Balance Card Well */}
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: '12px',
                            bgcolor: 'rgba(118, 118, 128, 0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: 1.25,
                          }}
                        >
                          <div>
                            <Typography sx={{ fontSize: '0.68rem', color: '#86868B', fontWeight: 600 }}>
                              Wallet Balance
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.95rem',
                                color: isLow ? '#FF9500' : '#34C759',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              ₦{provider.balance.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </div>
                          {usedInOtherRoute ? (
                            <Box
                              sx={{
                                px: 0.9,
                                py: 0.3,
                                borderRadius: '7px',
                                bgcolor: 'rgba(255, 149, 0, 0.1)',
                                color: '#FF9500',
                                fontSize: '0.675rem',
                                fontWeight: 700,
                              }}
                            >
                              {usedInOtherRoute}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                px: 0.9,
                                py: 0.3,
                                borderRadius: '7px',
                                bgcolor: provider.isActive ? 'rgba(52, 199, 89, 0.12)' : 'rgba(118, 118, 128, 0.12)',
                                color: provider.isActive ? '#34C759' : '#8E8E93',
                                fontSize: '0.675rem',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <CircleDot size={10} />
                              {provider.isActive ? 'Active' : 'Inactive'}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box
                sx={{
                  py: 7,
                  textAlign: 'center',
                  bgcolor: 'rgba(118, 118, 128, 0.05)',
                  borderRadius: '20px',
                  px: 3,
                }}
              >
                <Server size={38} color="#86868B" style={{ marginBottom: 12 }} />
                <Typography sx={{ fontWeight: 650, color: '#1D1D1F', mb: 0.5 }}>
                  {configuredProviders.length === 0
                    ? 'No Service Provider Set Up'
                    : 'No Service Providers Found'}
                </Typography>
                <Typography sx={{ fontSize: '0.825rem', color: '#86868B', mb: 2.5, maxWidth: 440, mx: 'auto' }}>
                  {configuredProviders.length === 0
                    ? 'You have not set up a service provider yet. Please configure a provider to route your services.'
                    : 'No service providers match your current search and filter settings.'}
                </Typography>
                {configuredProviders.length === 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setProviderModalOpen(false);
                      navigate('/dashboard/service-providers/add');
                    }}
                    startIcon={<Plus size={15} />}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 650,
                      bgcolor: '#0071E3',
                      color: '#FFFFFF',
                      px: 2.5,
                      py: 0.8,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#0077ED', boxShadow: 'none' },
                    }}
                  >
                    Set Up Service Provider
                  </Button>
                )}
                {configuredProviders.length > 0 && isFiltered && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleResetFilters}
                    startIcon={<RotateCcw size={14} />}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, color: '#1D1D1F' }}
                  >
                    Clear Search & Filters
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#FAFAFC', borderTop: '1px solid rgba(0, 0, 0, 0.06)', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#86868B', fontWeight: 500 }}>
            Only configured service providers with live credentials appear here.
          </Typography>
          <Button
            onClick={() => setProviderModalOpen(false)}
            variant="contained"
            size="small"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 650,
              bgcolor: '#1D1D1F',
              color: '#FFFFFF',
              px: 2.5,
              '&:hover': { bgcolor: '#3A3A3C' },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vendor Product / Plan Selection Modal - Apple Sheet Modal */}
      <Dialog
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            boxShadow: '0 30px 80px -20px rgba(0, 0, 0, 0.25)',
            maxHeight: '90vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 3, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '12px',
                  bgcolor: 'rgba(0, 113, 227, 0.08)',
                  color: '#0071E3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Package size={22} />
              </Box>
              <div>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#1D1D1F', letterSpacing: '-0.02em' }}>
                  Choose Vendor Product
                </Typography>
                <Typography variant="body2" sx={{ color: '#86868B', fontSize: '0.825rem' }}>
                  {getModalPlanTargetRouteTitle()}
                </Typography>
              </div>
            </Stack>

            <IconButton
              size="small"
              onClick={() => setPlanModalOpen(false)}
              sx={{
                width: 30,
                height: 30,
                bgcolor: 'rgba(118, 118, 128, 0.12)',
                color: '#1D1D1F',
                '&:hover': { bgcolor: 'rgba(118, 118, 128, 0.2)' },
              }}
            >
              <X size={16} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Stack spacing={2.5}>
            {/* Apple Search Input */}
            <TextField
              fullWidth
              size="small"
              placeholder={`Search ${activePlanRouteSlug ? activePlanRouteSlug.toUpperCase() : 'vendor'} products by name, code, or type (e.g. 1GB, SME)...`}
              value={planSearchQuery}
              onChange={(e) => setPlanSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={17} color="#86868B" />
                  </InputAdornment>
                ),
                endAdornment: planSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setPlanSearchQuery('')}>
                      <X size={15} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: 'rgba(118, 118, 128, 0.08)',
                  '& fieldset': { borderColor: 'transparent' },
                  '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.1)' },
                  '&.Mui-focused': {
                    bgcolor: '#FFFFFF',
                    '& fieldset': { borderColor: '#0071E3', borderWidth: '1.5px' },
                    boxShadow: '0 0 0 3px rgba(0, 113, 227, 0.15)',
                  },
                },
              }}
            />

            {/* Apple Filter & Sort Controls */}
            <Stack spacing={1.75}>
              {/* Row 1: Telecom Networks Segmented Control (if multi-network catalog detected) */}
              {availablePlanNetworks.length > 1 && (
                <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#86868B',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Globe size={13} color="#86868B" />
                    Network
                  </Typography>

                  <Box
                    sx={{
                      bgcolor: 'rgba(118, 118, 128, 0.1)',
                      borderRadius: '10px',
                      p: '3px',
                      display: 'inline-flex',
                      gap: '2px',
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => {
                        setNetworkFilter('all');
                        setPlanTypeFilter('all');
                      }}
                      sx={{
                        px: 1.5,
                        py: 0.35,
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: networkFilter === 'all' ? 700 : 500,
                        color: networkFilter === 'all' ? '#1D1D1F' : '#636366',
                        bgcolor: networkFilter === 'all' ? '#FFFFFF' : 'transparent',
                        boxShadow: networkFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        textTransform: 'none',
                      }}
                    >
                      All ({networkCounts.all || activePlanRoutePlans.length})
                    </Button>
                    {availablePlanNetworks.map((net) => (
                      <Button
                        key={net}
                        size="small"
                        onClick={() => {
                          setNetworkFilter(net);
                          setPlanTypeFilter('all');
                        }}
                        sx={{
                          px: 1.5,
                          py: 0.35,
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: networkFilter === net ? 700 : 500,
                          color: networkFilter === net ? '#1D1D1F' : '#636366',
                          bgcolor: networkFilter === net ? '#FFFFFF' : 'transparent',
                          boxShadow: networkFilter === net ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                          textTransform: 'none',
                        }}
                      >
                        {net} ({networkCounts[net] || 0})
                      </Button>
                    ))}
                  </Box>
                </Stack>
              )}

              {/* Row 2: Apple Horizontal Scrollable Pill Carousel + Popover Menu Button */}
              {planDataTypeOptions.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                  {/* Apple Popover Menu Trigger Button */}
                  <Button
                    size="small"
                    onClick={(e) => setCategoryMenuAnchor(e.currentTarget)}
                    startIcon={<SlidersHorizontal size={14} color={planTypeFilter === 'all' ? '#86868B' : '#0071E3'} />}
                    endIcon={<ChevronDown size={14} color={planTypeFilter === 'all' ? '#86868B' : '#0071E3'} />}
                    sx={{
                      flexShrink: 0,
                      px: 1.5,
                      py: 0.45,
                      borderRadius: '10px',
                      fontSize: '0.76rem',
                      fontWeight: 650,
                      textTransform: 'none',
                      bgcolor: planTypeFilter === 'all' ? 'rgba(118, 118, 128, 0.08)' : 'rgba(0, 113, 227, 0.08)',
                      color: planTypeFilter === 'all' ? '#1D1D1F' : '#0071E3',
                      border: '1px solid',
                      borderColor: planTypeFilter === 'all' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 113, 227, 0.3)',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        bgcolor: planTypeFilter === 'all' ? 'rgba(118, 118, 128, 0.14)' : 'rgba(0, 113, 227, 0.14)',
                      },
                    }}
                  >
                    Category: {planTypeFilter === 'all' ? `All (${networkFilteredPlansCount})` : planTypeFilter}
                  </Button>

                  {/* Horizontally Scrollable Apple Chips Bar (Zero Overflow, Smooth Touch/Mouse Wheel Scroll) */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      overflowX: 'auto',
                      py: 0.25,
                      minWidth: 0,
                      flex: 1,
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                      WebkitOverflowScrolling: 'touch',
                      flexWrap: 'nowrap',
                      maskImage: 'linear-gradient(to right, black calc(100% - 24px), transparent 100%)',
                      WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 24px), transparent 100%)',
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => setPlanTypeFilter('all')}
                      sx={{
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        px: 1.5,
                        py: 0.35,
                        borderRadius: '20px',
                        fontSize: '0.74rem',
                        fontWeight: planTypeFilter === 'all' ? 700 : 500,
                        bgcolor: planTypeFilter === 'all' ? '#1D1D1F' : 'rgba(118, 118, 128, 0.08)',
                        color: planTypeFilter === 'all' ? '#FFFFFF' : '#48484A',
                        textTransform: 'none',
                        border: '1px solid',
                        borderColor: planTypeFilter === 'all' ? '#1D1D1F' : 'rgba(0, 0, 0, 0.04)',
                        '&:hover': {
                          bgcolor: planTypeFilter === 'all' ? '#3A3A3C' : 'rgba(118, 118, 128, 0.15)',
                        },
                      }}
                    >
                      All Types ({networkFilteredPlansCount})
                    </Button>
                    {planDataTypeOptions.map((opt) => {
                      const isSelected = planTypeFilter.toLowerCase() === opt.name.toLowerCase();
                      return (
                        <Button
                          key={opt.name}
                          size="small"
                          onClick={() => setPlanTypeFilter(opt.name)}
                          sx={{
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            px: 1.5,
                            py: 0.35,
                            borderRadius: '20px',
                            fontSize: '0.74rem',
                            fontWeight: isSelected ? 700 : 500,
                            bgcolor: isSelected ? '#0071E3' : 'rgba(118, 118, 128, 0.08)',
                            color: isSelected ? '#FFFFFF' : '#48484A',
                            textTransform: 'none',
                            border: '1px solid',
                            borderColor: isSelected ? '#0071E3' : 'rgba(0, 0, 0, 0.04)',
                            boxShadow: isSelected ? '0 1px 4px rgba(0, 113, 227, 0.25)' : 'none',
                            '&:hover': {
                              bgcolor: isSelected ? '#005BB5' : 'rgba(118, 118, 128, 0.15)',
                            },
                          }}
                        >
                          {opt.name} ({opt.count})
                        </Button>
                      );
                    })}
                  </Box>
                </Stack>
              )}

              {/* Row 3: Sort Segmented Control & Status / Reset */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                {/* Sort Segmented Control */}
                <Box
                  sx={{
                    bgcolor: 'rgba(118, 118, 128, 0.1)',
                    borderRadius: '10px',
                    p: '3px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  <Box sx={{ pl: 1, pr: 0.5, color: '#86868B', display: 'flex', alignItems: 'center' }}>
                    <ArrowUpDown size={13} />
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setPlanSortBy('default')}
                    sx={{
                      px: 1.25,
                      py: 0.35,
                      borderRadius: '8px',
                      fontSize: '0.74rem',
                      fontWeight: planSortBy === 'default' ? 700 : 500,
                      color: planSortBy === 'default' ? '#1D1D1F' : '#636366',
                      bgcolor: planSortBy === 'default' ? '#FFFFFF' : 'transparent',
                      boxShadow: planSortBy === 'default' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      textTransform: 'none',
                    }}
                  >
                    Default
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setPlanSortBy('price_asc')}
                    sx={{
                      px: 1.25,
                      py: 0.35,
                      borderRadius: '8px',
                      fontSize: '0.74rem',
                      fontWeight: planSortBy === 'price_asc' ? 700 : 500,
                      color: planSortBy === 'price_asc' ? '#1D1D1F' : '#636366',
                      bgcolor: planSortBy === 'price_asc' ? '#FFFFFF' : 'transparent',
                      boxShadow: planSortBy === 'price_asc' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      textTransform: 'none',
                    }}
                  >
                    Lowest Price
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setPlanSortBy('price_desc')}
                    sx={{
                      px: 1.25,
                      py: 0.35,
                      borderRadius: '8px',
                      fontSize: '0.74rem',
                      fontWeight: planSortBy === 'price_desc' ? 700 : 500,
                      color: planSortBy === 'price_desc' ? '#1D1D1F' : '#636366',
                      bgcolor: planSortBy === 'price_desc' ? '#FFFFFF' : 'transparent',
                      boxShadow: planSortBy === 'price_desc' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      textTransform: 'none',
                    }}
                  >
                    Highest Price
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setPlanSortBy('name_asc')}
                    sx={{
                      px: 1.25,
                      py: 0.35,
                      borderRadius: '8px',
                      fontSize: '0.74rem',
                      fontWeight: planSortBy === 'name_asc' ? 700 : 500,
                      color: planSortBy === 'name_asc' ? '#1D1D1F' : '#636366',
                      bgcolor: planSortBy === 'name_asc' ? '#FFFFFF' : 'transparent',
                      boxShadow: planSortBy === 'name_asc' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      textTransform: 'none',
                    }}
                  >
                    A-Z
                  </Button>
                </Box>
              </Stack>
            </Stack>

            {/* Apple Popover Menu for Categories */}
            <Menu
              anchorEl={categoryMenuAnchor}
              open={Boolean(categoryMenuAnchor)}
              onClose={() => setCategoryMenuAnchor(null)}
              PaperProps={{
                sx: {
                  borderRadius: '16px',
                  boxShadow: '0 20px 50px -10px rgba(0, 0, 0, 0.22)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  minWidth: 260,
                  maxHeight: 360,
                  p: 0.75,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  setPlanTypeFilter('all');
                  setCategoryMenuAnchor(null);
                }}
                sx={{
                  borderRadius: '10px',
                  py: 1,
                  px: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: planTypeFilter === 'all' ? 'rgba(0, 113, 227, 0.08)' : 'transparent',
                  color: planTypeFilter === 'all' ? '#0071E3' : '#1D1D1F',
                  fontWeight: planTypeFilter === 'all' ? 700 : 500,
                  '&:hover': { bgcolor: 'rgba(0, 113, 227, 0.06)' },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {planTypeFilter === 'all' ? <Check size={16} color="#0071E3" /> : <Box sx={{ width: 16 }} />}
                  <Typography sx={{ fontSize: '0.825rem', fontWeight: planTypeFilter === 'all' ? 700 : 500 }}>
                    All Categories
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    px: 0.8,
                    py: 0.2,
                    borderRadius: '6px',
                    bgcolor: 'rgba(118, 118, 128, 0.1)',
                    fontSize: '0.675rem',
                    fontWeight: 650,
                    color: '#636366',
                  }}
                >
                  {networkFilteredPlansCount}
                </Box>
              </MenuItem>

              <Divider sx={{ my: 0.5, borderColor: 'rgba(0, 0, 0, 0.06)' }} />

              {planDataTypeOptions.map((opt) => {
                const isSelected = planTypeFilter.toLowerCase() === opt.name.toLowerCase();
                return (
                  <MenuItem
                    key={opt.name}
                    onClick={() => {
                      setPlanTypeFilter(opt.name);
                      setCategoryMenuAnchor(null);
                    }}
                    sx={{
                      borderRadius: '10px',
                      py: 0.85,
                      px: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: isSelected ? 'rgba(0, 113, 227, 0.08)' : 'transparent',
                      color: isSelected ? '#0071E3' : '#1D1D1F',
                      fontWeight: isSelected ? 700 : 500,
                      '&:hover': { bgcolor: 'rgba(0, 113, 227, 0.06)' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {isSelected ? <Check size={16} color="#0071E3" /> : <Box sx={{ width: 16 }} />}
                      <Typography sx={{ fontSize: '0.825rem', fontWeight: isSelected ? 700 : 500 }}>
                        {opt.name}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        px: 0.8,
                        py: 0.2,
                        borderRadius: '6px',
                        bgcolor: isSelected ? 'rgba(0, 113, 227, 0.15)' : 'rgba(118, 118, 128, 0.1)',
                        fontSize: '0.675rem',
                        fontWeight: 650,
                        color: isSelected ? '#0071E3' : '#636366',
                      }}
                    >
                      {opt.count}
                    </Box>
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Results Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography sx={{ fontSize: '0.8rem', color: '#86868B', fontWeight: 500 }}>
                Showing <strong>{filteredPlanList.length}</strong> of {activePlanRoutePlans.length} products from {activePlanRouteSlug.toUpperCase()}
              </Typography>
              {isPlanFiltered && (
                <Button
                  size="small"
                  startIcon={<RotateCcw size={12} />}
                  onClick={handleResetPlanFilters}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', p: 0, color: '#0071E3', fontWeight: 600 }}
                >
                  Reset Filters
                </Button>
              )}
            </Stack>

            {/* Plan Catalog Grid / Loading State */}
            {renderPlanCatalogContent()}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#FAFAFC', borderTop: '1px solid rgba(0, 0, 0, 0.06)', justifyContent: 'space-between' }}>
          <Button
            size="small"
            startIcon={<Edit3 size={14} />}
            onClick={() => {
              if (targetPlanRouteIndex !== null) {
                handleRouteFieldChange(targetPlanRouteIndex, 'isCustomId', true);
              }
              setPlanModalOpen(false);
            }}
            sx={{ textTransform: 'none', color: '#0071E3', fontWeight: 600, fontSize: '0.825rem' }}
          >
            Enter Plan Code Manually Instead
          </Button>

          <Button
            onClick={() => setPlanModalOpen(false)}
            variant="contained"
            size="small"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 650,
              bgcolor: '#1D1D1F',
              color: '#FFFFFF',
              px: 2.5,
              '&:hover': { bgcolor: '#3A3A3C' },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Route Priority Selector Popover Menu */}
      <Menu
        anchorEl={priorityMenuAnchor}
        open={Boolean(priorityMenuAnchor)}
        onClose={() => {
          setPriorityMenuAnchor(null);
          setPriorityMenuRouteIdx(null);
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 50px -10px rgba(0, 0, 0, 0.22)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            minWidth: 260,
            p: 0.75,
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.06)', mb: 0.5 }}>
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#86868B',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Change Route Priority
          </Typography>
        </Box>
        {processorRoutes.map((_, targetIdx) => {
          const isCurrent = priorityMenuRouteIdx === targetIdx;
          const targetSlotLabel =
            targetIdx === 0
              ? 'Priority 1 (Initial Attempt)'
              : `Priority ${targetIdx + 1} (Failover #${targetIdx})`;

          return (
            <MenuItem
              key={targetIdx}
              onClick={() => {
                handleMoveRoute(priorityMenuRouteIdx, targetIdx);
                setPriorityMenuAnchor(null);
                setPriorityMenuRouteIdx(null);
              }}
              sx={{
                borderRadius: '10px',
                py: 0.85,
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: isCurrent ? 'rgba(0, 113, 227, 0.08)' : 'transparent',
                color: isCurrent ? '#0071E3' : '#1D1D1F',
                fontWeight: isCurrent ? 700 : 500,
                fontSize: '0.825rem',
                '&:hover': { bgcolor: 'rgba(0, 113, 227, 0.06)' },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                {isCurrent ? <Check size={15} color="#0071E3" /> : <Box sx={{ width: 15 }} />}
                <span>{targetSlotLabel}</span>
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </Card>
  );
}

MultiProcessorRoutingCard.propTypes = {
  useV2Engine: PropTypes.bool.isRequired,
  onChangeUseV2Engine: PropTypes.func.isRequired,
  processorRoutes: PropTypes.array.isRequired,
  onChangeRoutes: PropTypes.func.isRequired,
  selectedService: PropTypes.string,
  providers: PropTypes.array,
  onSyncPlan: PropTypes.func,
  onPrimaryPlanChange: PropTypes.func,
  onPrimaryNetworkChange: PropTypes.func,
  fallbackPlanId: PropTypes.string,
  fallbackNetworkId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fallbackBuyingAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
};
